import React, { createContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  hasLanguagePrefix, 
  getLanguageFromPath, 
  removeLanguagePrefix,
  getLocalizedPath
} from '@/utils/languageUtils';
import translations from '@/translations/homepage';
import { backofficeEn } from '@/translations/backoffice/en';
import { backofficeIt } from '@/translations/backoffice/it';
import { backofficeEs } from '@/translations/backoffice/es';
import { backofficeDe } from '@/translations/backoffice/de';

import { backofficeTr } from '@/translations/backoffice/tr';
import { adminEn } from '@/translations/admin/en';
import { adminDe } from '@/translations/admin/de';
import { adminEs } from '@/translations/admin/es';
import { adminIt } from '@/translations/admin/it';

import { adminTr } from '@/translations/admin/tr';
import { Language, SUPPORTED_LANGUAGES } from '@/types/language';

// Combine homepage, backoffice, and admin translations
const allTranslations = {
  en: { ...translations.en, ...backofficeEn, ...adminEn },
  it: { ...translations.it, ...backofficeIt, ...adminIt },
  es: { ...translations.es, ...backofficeEs, ...adminEs },
  de: { ...translations.de, ...backofficeDe, ...adminDe },
  
  tr: { ...translations.tr, ...backofficeTr, ...adminTr },
};

// Define the context type
type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
};

// Create context with default values
export const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
});

type LanguageProviderProps = {
  children: React.ReactNode;
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize language from URL or localStorage
  const initializeLanguage = (): Language => {
    // Check URL first
    const pathLang = getLanguageFromPath(window.location.pathname);
    
    if (pathLang) {
      // Save to localStorage if found in URL
      localStorage.setItem('language', pathLang);
      return pathLang;
    }
    
    // Check localStorage
    const savedLang = localStorage.getItem('language') as Language;
    
    if (savedLang && (SUPPORTED_LANGUAGES as readonly string[]).includes(savedLang)) {
      return savedLang;
    }
    
    // Check browser language
    const browserLang = navigator.language.split('-')[0] as Language;
    
    if ((SUPPORTED_LANGUAGES as readonly string[]).includes(browserLang)) {
      return browserLang;
    }
    
    // Default to English
    return 'en';
  };
  
  const [language, setLanguageState] = useState<Language>(initializeLanguage);
  
  // Update URL when language changes
  useEffect(() => {
    const currentLang = getLanguageFromPath(location.pathname);
    
    // For English, paths intentionally have no prefix.
    // Treat no-prefix paths as English to avoid infinite navigation loops.
    const effectiveLang = currentLang || 'en';
    
    if (effectiveLang !== language) {
      const newPath = getLocalizedPath(
        removeLanguagePrefix(location.pathname), 
        language,
        location.search
      );
      
      console.log(`[LanguageContext] Redirecting from ${location.pathname} to ${newPath} (lang: ${effectiveLang} -> ${language})`);
      // Preserve hash fragment (critical for Supabase auth recovery tokens)
      navigate(newPath + (location.hash || ''), { replace: true });
    }
  }, [language, location.pathname, location.search, navigate]);
  
  // Update language and localStorage
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };
  
  // Enhanced translation function that supports both nested keys and flat keys
  const t = (key: string): string => {
    if (!key) return '';
    
    // Support nested keys with dot notation
    const getNestedValue = (obj: any, path: string): string | undefined => {
      return path.split('.').reduce((current, segment) => {
        return current && current[segment] !== undefined ? current[segment] : undefined;
      }, obj);
    };
    
    // Check if translation exists for current language
    if (allTranslations[language]) {
      // First try to get the value as-is (for flat keys like sidebar translations)
      if (allTranslations[language][key] !== undefined) {
        return allTranslations[language][key];
      }
      
      // Then try nested lookup (for dot notation keys)
      const value = getNestedValue(allTranslations[language], key);
      if (value !== undefined) {
        return value;
      }
    }
    
    // Fallback to English
    if (allTranslations['en']) {
      // First try flat key lookup
      if (allTranslations['en'][key] !== undefined) {
        return allTranslations['en'][key];
      }
      
      // Then try nested lookup
      const value = getNestedValue(allTranslations['en'], key);
      if (value !== undefined) {
        return value;
      }
    }
    
    // If no translation is found, return the key itself
    console.log(`[LanguageProvider] Translation missing for key: ${key}`);
    return key;
  };
  
  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Export useLanguage hook
export const useLanguage = () => {
  const context = React.useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export { translations };
